import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import FormTextField from '../../../components/ui/form/FormTextField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { groupFormSchema, type GroupFormValues } from './groupFormSchema'
import type { Group } from '../types'

interface Props {
  visible: boolean
  group: Group | null
  submitting: boolean
  nameError?: string | null
  onClearNameError: () => void
  onSubmit: (values: { nameTr: string; nameEn: string | null }) => void
  onHide: () => void
}

/** Grup adı düzenleme (122) — grup için düzenlenebilen tek şey adıdır (TR zorunlu, EN opsiyonel). */
export default function GroupFormDialog({
  visible,
  group,
  submitting,
  nameError,
  onClearNameError,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setError } = useZodForm(groupFormSchema, {
    defaultValues: { nameTr: '', nameEn: '' },
  })

  useEffect(() => {
    if (visible) reset({ nameTr: group?.nameTr ?? '', nameEn: group?.nameEn ?? '' })
  }, [visible, group, reset])

  useEffect(() => {
    if (nameError) {
      setError('nameTr', { type: 'server', message: nameError })
      onClearNameError()
    }
  }, [nameError, setError, onClearNameError])

  const onValid = (v: GroupFormValues) => onSubmit({ nameTr: v.nameTr, nameEn: v.nameEn ? v.nameEn : null })

  return (
    <FormDialog
      visible={visible}
      header={t('admin.groups.form.title')}
      submitLabel={t('admin.groups.form.save')}
      submitting={submitting}
      submitDisabled={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
    >
      <div className="admin-form">
        <FormTextField control={control} name="nameTr" label={t('admin.groups.form.nameTr')} required autoFocus />
        <FormTextField control={control} name="nameEn" label={t('admin.groups.form.nameEn')} />
      </div>
    </FormDialog>
  )
}
